import { useState, useEffect, useRef, useMemo } from 'react';
import {
  listOrgCollection,
  readOrgFile,
  writeOrgFile,
  writeOrgBinary,
  orgBinaryUrl,
} from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import {
  BTN_PRIMARY, BTN_SECONDARY,
  BG_CONTENT, BG_SURFACE, SURFACE_WHITE, BG_MUTED,
  BORDER_DEFAULT, TEXT_DEFAULT, TEXT_MUTED, TEXT_HEADING,
  BRAND_PRIMARY, ERROR_TEXT,
  FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_SECTION_TITLE,
} from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    attach:           'Adjuntar evidencia',
    addAnother:       'Añadir otra',
    openFile:         'Abrir archivo',
    editMetadata:     'Editar metadatos',
    remove:           'Quitar',
    replace:          'Sustituir por otra',
    change:           'Cambiar',
    unresolved:       '⚠ No resuelta:',
    noAttachment:     'Sin adjunto',
    modalCreateTitle: 'Adjuntar evidencia',
    modalEditTitle:   'Editar metadatos de evidencia',
    tabCreate:        'Crear nueva',
    tabPick:          'Seleccionar existente',
    fTitle:           'Título',
    fType:            'Tipo',
    fFile:            'Archivo (opcional)',
    fDescription:     'Descripción',
    pickFile:         'Elegir archivo',
    noFileChosen:     'Ningún archivo seleccionado',
    searchPlaceholder:'Buscar por referencia, título o tipo…',
    emptyList:        'No hay evidencias disponibles.',
    save:             'Guardar',
    cancel:           'Cancelar',
    required:         'Obligatorio',
    writeError:       'No se pudo guardar la evidencia. Comprueba el acceso a la carpeta del SGSI.',
  },
  en: {
    attach:           'Attach evidence',
    addAnother:       'Add another',
    openFile:         'Open file',
    editMetadata:     'Edit metadata',
    remove:           'Remove',
    replace:          'Replace with another',
    change:           'Change',
    unresolved:       '⚠ Unresolved:',
    noAttachment:     'No attachment',
    modalCreateTitle: 'Attach evidence',
    modalEditTitle:   'Edit evidence metadata',
    tabCreate:        'Create new',
    tabPick:          'Pick existing',
    fTitle:           'Title',
    fType:            'Type',
    fFile:            'File (optional)',
    fDescription:     'Description',
    pickFile:         'Choose file',
    noFileChosen:     'No file chosen',
    searchPlaceholder:'Search by ref, title or type…',
    emptyList:        'No evidence records available.',
    save:             'Save',
    cancel:           'Cancel',
    required:         'Required',
    writeError:       'Could not save the evidence. Check access to the ISMS folder.',
  },
};

// Evidence type enum per GRC schema.
const EVIDENCE_TYPES = ['Document', 'Screenshot', 'Log', 'Report', 'Certificate', 'Record', 'Other'];

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayIso() { return new Date().toISOString().slice(0, 10); }

function nextEvidenceFileId(evidences, today) {
  const dayPrefix = `evd-${today}-`;
  let max = 0;
  evidences.forEach((e) => {
    if (e.id && e.id.startsWith(dayPrefix)) {
      const n = parseInt(e.id.slice(dayPrefix.length), 10);
      if (!isNaN(n) && n > max) max = n;
    }
  });
  return `${dayPrefix}${String(max + 1).padStart(3, '0')}`;
}

function nextEvidenceRef(evidences, refPrefix, year) {
  const yearPrefix = `${refPrefix}-${year}-`;
  let max = 0;
  evidences.forEach((e) => {
    if (e.ref && e.ref.startsWith(yearPrefix)) {
      const n = parseInt(e.ref.slice(yearPrefix.length), 10);
      if (!isNaN(n) && n > max) max = n;
    }
  });
  return `${yearPrefix}${String(max + 1).padStart(3, '0')}`;
}

async function sha256Hex(arrayBuffer) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const bytes = new Uint8Array(hashBuffer);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

function fileExtensionFromName(name) {
  if (!name) return '';
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return '';
  return name.slice(idx + 1).toLowerCase();
}

// ── PUBLIC HELPERS ────────────────────────────────────────────────────────────

// If value matches an evidence.id, return that record. Otherwise return null.
export function resolveEvidence(value, evidences) {
  if (!value || !Array.isArray(evidences)) return null;
  return evidences.find((e) => e.id === value) || null;
}

// "<ref> — <title>" or "" if evidence is null/undefined.
export function evidenceDisplayLabel(evidence) {
  if (!evidence) return '';
  return `${evidence.ref || ''} — ${evidence.title || ''}`;
}

// ── EVIDENCE SELECTOR ─────────────────────────────────────────────────────────
export default function EvidenceSelector({
  orgId,
  lang,
  value,
  onChange,
  multiple = false,
  disabled = false,
  density = 'full',
}) {
  if (density !== 'full' && density !== 'compact') {
    throw new Error(`EvidenceSelector: invalid 'density' prop "${String(density)}". Accepted values: 'full', 'compact'.`);
  }
  if (multiple && density === 'compact') {
    throw new Error("EvidenceSelector: 'density=compact' is single-mode only. The combination multiple=true + density='compact' is not supported.");
  }
  const t = T[lang] || T.en;

  const [evidences, setEvidences] = useState([]);
  const [modalMode, setModalMode] = useState(null);  // null | 'create-or-pick' | 'edit-metadata'
  const [modalTarget, setModalTarget] = useState(null); // 'replace' | 'add' | id | null
  const [refPrefix, setRefPrefix] = useState('evd');

  // Reload evidences from disk.
  const reloadEvidences = async () => {
    try {
      const data = await listOrgCollection(orgId, 'evidence');
      setEvidences(Array.isArray(data) ? data : []);
    } catch {
      setEvidences([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    listOrgCollection(orgId, 'evidence')
      .then((data) => { if (!cancelled) setEvidences(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setEvidences([]); });
    return () => { cancelled = true; };
  }, [orgId]);

  // Read refPrefix from profile.json (default 'evd').
  useEffect(() => {
    let cancelled = false;
    readOrgFile(orgId, 'profile.json')
      .then((profile) => {
        if (cancelled) return;
        const p = profile && profile.refPrefixes && profile.refPrefixes.evidence;
        setRefPrefix(p || 'evd');
      })
      .catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, [orgId]);

  const selectedArr = multiple ? (Array.isArray(value) ? value : []) : [];

  const openCreateOrPick = (target) => {
    if (disabled) return;
    setModalTarget(target);
    setModalMode('create-or-pick');
  };

  const openEditMetadata = (id) => {
    if (disabled) return;
    setModalTarget(id);
    setModalMode('edit-metadata');
  };

  const closeModal = () => {
    setModalMode(null);
    setModalTarget(null);
  };

  const handleOpenFile = async (filePath) => {
    if (!filePath) return;
    const url = await orgBinaryUrl(orgId, filePath);
    if (!url) return;
    window.open(url, '_blank');
    // Best-effort revoke after the new tab consumes the URL.
    setTimeout(() => { URL.revokeObjectURL(url); }, 60000);
  };

  const handleRemoveSingle = () => {
    if (disabled) return;
    onChange(null);
  };

  const handleRemoveFromArray = (id) => {
    if (disabled) return;
    const arr = Array.isArray(value) ? value : [];
    onChange(arr.filter((x) => x !== id));
  };

  // ── Modal save handlers ──────────────────────────────────────────────────
  const handleCreateNew = async ({ title, type, file, description }) => {
    const today = todayIso();
    const year = new Date().getFullYear();
    const id = nextEvidenceFileId(evidences, today);
    const ref = nextEvidenceRef(evidences, refPrefix, year);

    let filePath = null;
    let checksum = null;
    if (file) {
      const ext = fileExtensionFromName(file.name);
      filePath = ext ? `evidence/files/${id}.${ext}` : `evidence/files/${id}`;
      const buf = await file.arrayBuffer();
      checksum = await sha256Hex(buf);
    }

    const nowIso = new Date().toISOString();
    const record = {
      id,
      orgId,
      ref,
      title,
      description: description || '',
      type,
      confidentiality: null,
      linkedControlIds: [],
      clauseRefs: [],
      linkedAuditIds: [],
      linkedNonconformityIds: [],
      linkedIncidentIds: [],
      generatedBy: 'manual',
      collectionDate: today,
      collectedBy: null,
      filePath,
      expiryDate: null,
      retentionPeriod: null,
      retentionEndDate: null,
      checksum,
      notes: '',
      changeLog: [{
        timestamp: nowIso,
        field: 'id',
        previousValue: null,
        newValue: id,
        changedBy: 'system',
      }],
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    if (file && filePath) {
      await writeOrgBinary(orgId, filePath, file);
    }
    await writeOrgFile(orgId, `evidence/${id}.json`, record);
    await reloadEvidences();

    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      onChange([...arr, record.id]);
    } else {
      onChange(record.id);
    }
    closeModal();
  };

  const handlePickExisting = (selectedId) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.includes(selectedId)) onChange([...arr, selectedId]);
    } else {
      onChange(selectedId);
    }
    closeModal();
  };

  // edit-metadata: update title/type/description on the current Evidence record.
  const handleEditMetadata = async ({ title, type, description }) => {
    const id = modalTarget;
    const current = evidences.find((e) => e.id === id);
    if (!current) { closeModal(); return; }
    const nowIso = new Date().toISOString();
    const incoming = {
      ...current,
      title,
      type,
      description: description || '',
      updatedAt: nowIso,
    };
    incoming.changeLog = appendChangeLog(current, { title, type, description: description || '' }, 'system');
    await writeOrgFile(orgId, `evidence/${id}.json`, incoming);
    await reloadEvidences();
    closeModal();
    // Do NOT call onChange — the id has not changed.
  };

  // ── Render helpers ───────────────────────────────────────────────────────
  const renderCard = (id, onRemove) => {
    const evidence = resolveEvidence(id, evidences);
    const unresolved = !evidence;
    return (
      <div
        key={id || '__none'}
        style={{
          background: BG_SURFACE,
          border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 8,
          padding: '0.6rem 0.75rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '0.75rem',
          marginBottom: '0.5rem',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {unresolved ? (
            <div style={{ ...FONT_LABEL, color: ERROR_TEXT }}>
              {t.unresolved} {String(id)}
            </div>
          ) : (
            <>
              <div style={{ ...FONT_LABEL, color: TEXT_DEFAULT, fontWeight: 600 }}>
                {evidence.ref} — {evidence.title}
              </div>
              <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginTop: '0.15rem' }}>
                {evidence.type}{' '}
                <span style={{ marginLeft: '0.4rem' }}>
                  {evidence.filePath ? '📎' : '✎'}
                </span>
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            type="button"
            disabled={disabled || unresolved || !evidence?.filePath}
            onClick={() => handleOpenFile(evidence?.filePath)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_TINY,
              borderRadius: 6,
              padding: '0.25rem 0.55rem',
              cursor: (disabled || unresolved || !evidence?.filePath) ? 'not-allowed' : 'pointer',
              opacity: (disabled || unresolved || !evidence?.filePath) ? 0.55 : 1,
            }}
          >
            {t.openFile}
          </button>
          <button
            type="button"
            disabled={disabled || unresolved}
            onClick={() => openEditMetadata(id)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_TINY,
              borderRadius: 6,
              padding: '0.25rem 0.55rem',
              cursor: (disabled || unresolved) ? 'not-allowed' : 'pointer',
              opacity: (disabled || unresolved) ? 0.55 : 1,
            }}
          >
            {t.editMetadata}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onRemove}
            style={{
              ...BTN_SECONDARY,
              ...FONT_TINY,
              borderRadius: 6,
              padding: '0.25rem 0.55rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {t.remove}
          </button>
          {!multiple && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openCreateOrPick('replace')}
              style={{
                ...BTN_SECONDARY,
                ...FONT_TINY,
                borderRadius: 6,
                padding: '0.25rem 0.55rem',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {t.replace}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Compact card render (compact density, single-mode only) ─────────────
  const renderCompactCard = (id) => {
    const evidence = resolveEvidence(id, evidences);
    const unresolved = !evidence;
    const openDisabled = disabled || unresolved || !evidence?.filePath;
    return (
      <div
        style={{
          background: BG_SURFACE,
          border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 6,
          padding: '0.35rem 0.6rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <div
          style={{
            ...FONT_LABEL,
            color: unresolved ? ERROR_TEXT : TEXT_DEFAULT,
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {unresolved ? `${t.unresolved} ${String(id)}` : evidence.ref}
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
          <button
            type="button"
            disabled={openDisabled}
            onClick={() => handleOpenFile(evidence?.filePath)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_TINY,
              borderRadius: 6,
              padding: '0.25rem 0.55rem',
              cursor: openDisabled ? 'not-allowed' : 'pointer',
              opacity: openDisabled ? 0.55 : 1,
            }}
          >
            {t.openFile}
          </button>
          <button
            type="button"
            disabled={disabled || unresolved}
            onClick={() => openEditMetadata(id)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_TINY,
              borderRadius: 6,
              padding: '0.25rem 0.55rem',
              cursor: (disabled || unresolved) ? 'not-allowed' : 'pointer',
              opacity: (disabled || unresolved) ? 0.55 : 1,
            }}
          >
            {t.editMetadata}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => openCreateOrPick('replace')}
            style={{
              ...BTN_SECONDARY,
              ...FONT_TINY,
              borderRadius: 6,
              padding: '0.25rem 0.55rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.55 : 1,
            }}
          >
            {t.change}
          </button>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Single mode (full density): empty state */}
      {!multiple && !value && density === 'full' && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => openCreateOrPick('attach')}
          style={{
            ...BTN_PRIMARY,
            ...FONT_BUTTON,
            border: 'none',
            borderRadius: 8,
            padding: '0.5rem 1rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {t.attach}
        </button>
      )}

      {/* Single mode (compact density): empty state */}
      {!multiple && !value && density === 'compact' && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => openCreateOrPick('attach')}
          style={{
            ...BTN_SECONDARY,
            ...FONT_BUTTON,
            borderRadius: 6,
            padding: '0.35rem 0.7rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {t.attach}
        </button>
      )}

      {/* Single mode: card */}
      {!multiple && value && density === 'full' && renderCard(value, handleRemoveSingle)}
      {!multiple && value && density === 'compact' && renderCompactCard(value)}

      {/* Multi mode: list of cards */}
      {multiple && (
        <>
          {selectedArr.map((id) => renderCard(id, () => handleRemoveFromArray(id)))}
          <button
            type="button"
            disabled={disabled}
            onClick={() => openCreateOrPick('add')}
            style={{
              ...BTN_SECONDARY,
              ...FONT_BUTTON,
              borderRadius: 8,
              padding: '0.4rem 0.9rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {t.addAnother}
          </button>
        </>
      )}

      {/* Modal */}
      {modalMode && (
        <EvidenceModal
          mode={modalMode}
          t={t}
          orgId={orgId}
          evidences={evidences}
          excludedIds={multiple ? selectedArr : (value ? [value] : [])}
          targetId={modalMode === 'edit-metadata' ? modalTarget : null}
          onCancel={closeModal}
          onCreate={handleCreateNew}
          onPick={handlePickExisting}
          onEditMetadata={handleEditMetadata}
        />
      )}
    </div>
  );
}

// ── EVIDENCE MODAL ────────────────────────────────────────────────────────────
function EvidenceModal({ mode, t, evidences, excludedIds, targetId, onCancel, onCreate, onPick, onEditMetadata }) {
  const isEdit = mode === 'edit-metadata';
  const editingRecord = useMemo(
    () => isEdit ? evidences.find((e) => e.id === targetId) : null,
    [isEdit, evidences, targetId]
  );

  const [tab, setTab] = useState('create');
  const [title, setTitle] = useState(editingRecord ? (editingRecord.title || '') : '');
  const [type, setType]   = useState(editingRecord ? (editingRecord.type  || 'Document') : 'Document');
  const [description, setDescription] = useState(editingRecord ? (editingRecord.description || '') : '');
  const [pickedFile, setPickedFile] = useState(null);
  const [search, setSearch] = useState('');
  const [pickedExistingId, setPickedExistingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [writeError, setWriteError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const filteredEvidences = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...evidences]
      .filter((e) => !excludedIds.includes(e.id))
      .filter((e) => {
        if (!q) return true;
        return (
          (e.ref || '').toLowerCase().includes(q) ||
          (e.title || '').toLowerCase().includes(q) ||
          (e.type || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [evidences, excludedIds, search]);

  const handleSave = async () => {
    setWriteError(null);
    setValidationError(null);
    if (isEdit) {
      if (!title.trim()) { setValidationError('title'); return; }
      if (!type) { setValidationError('type'); return; }
      try {
        setSubmitting(true);
        await onEditMetadata({ title: title.trim(), type, description });
      } catch {
        setWriteError(t.writeError);
        setSubmitting(false);
      }
      return;
    }
    if (tab === 'create') {
      if (!title.trim()) { setValidationError('title'); return; }
      if (!type) { setValidationError('type'); return; }
      try {
        setSubmitting(true);
        await onCreate({ title: title.trim(), type, file: pickedFile, description });
      } catch {
        setWriteError(t.writeError);
        setSubmitting(false);
      }
    } else {
      if (!pickedExistingId) { setValidationError('pick'); return; }
      onPick(pickedExistingId);
    }
  };

  const labelSt = { ...FONT_TINY, color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem', display: 'block' };
  const inputSt = { ...FONT_LABEL, width: '100%', boxSizing: 'border-box', padding: '0.45rem 0.65rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: SURFACE_WHITE, color: TEXT_DEFAULT, outline: 'none' };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 34, 53, 0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: BG_SURFACE,
          border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 10,
          padding: '1.5rem 1.75rem',
          maxWidth: 560,
          width: '92%',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        <h3 style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, margin: '0 0 1rem' }}>
          {isEdit ? t.modalEditTitle : t.modalCreateTitle}
        </h3>

        {/* Tabs (create-or-pick mode only) */}
        {!isEdit && (
          <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER_DEFAULT}`, marginBottom: '1rem' }}>
            {[
              { key: 'create', label: t.tabCreate },
              { key: 'pick',   label: t.tabPick },
            ].map(({ key, label }) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setTab(key); setValidationError(null); setWriteError(null); }}
                  style={{
                    ...FONT_BUTTON,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active ? `2px solid ${BRAND_PRIMARY}` : '2px solid transparent',
                    color: active ? BRAND_PRIMARY : TEXT_MUTED,
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    marginBottom: -1,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Create tab / edit-metadata fields */}
        {(isEdit || tab === 'create') && (
          <div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label style={labelSt}>
                {t.fTitle} <span style={{ color: ERROR_TEXT, textTransform: 'none' }}>*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setValidationError(null); }}
                style={{ ...inputSt, borderColor: validationError === 'title' ? ERROR_TEXT : BORDER_DEFAULT }}
              />
            </div>

            <div style={{ marginBottom: '0.85rem' }}>
              <label style={labelSt}>
                {t.fType} <span style={{ color: ERROR_TEXT, textTransform: 'none' }}>*</span>
              </label>
              <select
                value={type}
                onChange={(e) => { setType(e.target.value); setValidationError(null); }}
                style={{ ...inputSt, cursor: 'pointer' }}
              >
                {EVIDENCE_TYPES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            {/* File picker (create mode) or read-only summary (edit-metadata) */}
            {!isEdit ? (
              <div style={{ marginBottom: '0.85rem' }}>
                <label style={labelSt}>{t.fFile}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{
                      ...BTN_SECONDARY,
                      ...FONT_BUTTON,
                      borderRadius: 6,
                      padding: '0.35rem 0.75rem',
                      cursor: 'pointer',
                    }}
                  >
                    {t.pickFile}
                  </button>
                  <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
                    {pickedFile ? pickedFile.name : t.noFileChosen}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => setPickedFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                  />
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '0.85rem' }}>
                <label style={labelSt}>{t.fFile}</label>
                <div style={{
                  ...FONT_SMALL,
                  color: TEXT_DEFAULT,
                  background: BG_MUTED,
                  border: `1px solid ${BORDER_DEFAULT}`,
                  borderRadius: 6,
                  padding: '0.45rem 0.65rem',
                }}>
                  {editingRecord && editingRecord.filePath ? (
                    <>
                      📎 {editingRecord.filePath.split('/').pop()}
                      {editingRecord.checksum ? (
                        <span style={{ marginLeft: '0.5rem', color: TEXT_MUTED }}>
                          — SHA-256: {editingRecord.checksum.slice(0, 12)}…
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span style={{ color: TEXT_MUTED }}>{t.noAttachment}</span>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={labelSt}>{t.fDescription}</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ ...inputSt, resize: 'vertical' }}
              />
            </div>
          </div>
        )}

        {/* Pick existing tab */}
        {!isEdit && tab === 'pick' && (
          <div>
            <div style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
                style={inputSt}
              />
            </div>
            <div style={{
              border: `1px solid ${BORDER_DEFAULT}`,
              borderRadius: 6,
              maxHeight: 280,
              overflowY: 'auto',
              background: SURFACE_WHITE,
            }}>
              {filteredEvidences.length === 0 ? (
                <div style={{ ...FONT_SMALL, color: TEXT_MUTED, padding: '0.75rem' }}>
                  {t.emptyList}
                </div>
              ) : filteredEvidences.map((ev) => {
                const selected = pickedExistingId === ev.id;
                return (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => { setPickedExistingId(ev.id); setValidationError(null); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      background: selected ? BG_CONTENT : 'transparent',
                      border: 'none',
                      borderBottom: `1px solid ${BORDER_DEFAULT}`,
                      padding: '0.55rem 0.75rem',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ ...FONT_LABEL, color: TEXT_DEFAULT, fontWeight: selected ? 700 : 400 }}>
                      {ev.ref} — {ev.title}
                    </div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginTop: '0.15rem' }}>
                      {ev.type} <span style={{ marginLeft: '0.4rem' }}>{ev.filePath ? '📎' : '✎'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {validationError === 'pick' && (
              <div style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.5rem' }}>
                {t.required}
              </div>
            )}
          </div>
        )}

        {/* Validation error for create-mode required fields */}
        {(validationError === 'title' || validationError === 'type') && (
          <div style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.25rem' }}>
            {t.required}
          </div>
        )}

        {/* Write error */}
        {writeError && (
          <div style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.5rem' }}>
            {writeError}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              ...BTN_SECONDARY,
              ...FONT_BUTTON,
              borderRadius: 8,
              padding: '0.5rem 1.1rem',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none',
              borderRadius: 8,
              padding: '0.5rem 1.1rem',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {t.save}
          </button>
        </div>
      </div>
    </div>
  );
}
